// Mock Wallet Data Manager
// This utility helps manage mock data for wallet features during development

const MOCK_DATA_KEY = 'THAKLIS_MOCK_WALLET_DATA';

// Initialize mock data structure
const initializeMockData = () => {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
  const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
  const fourDaysAgo = new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000);
  const fiveDaysAgo = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);

  return {
    enabled: false,
    balance: 3526.50, // Current balance after all transactions
    transactions: [
      // Credit: Topup via Stripe (3 days ago)
      {
        id: 'txn-001',
        date: threeDaysAgo.toISOString().split('T')[0],
        type: 'CREDIT',
        description: 'Wallet Topup via Stripe',
        amount: 485.50,
        status: 'COMPLETED',
        createDateTime: threeDaysAgo.toISOString(),
        customerPaymentMethod: 'Stripe',
        paymentSource: 'Customer Credit Card ending in 4242',
        transactionId: 'ch_mock123456',
        gatewayFee: 14.50,
        grossAmount: 500.00
      },
      // Credit: Topup via Bank Transfer (Yesterday)
      {
        id: 'txn-002',
        date: yesterday.toISOString().split('T')[0],
        type: 'CREDIT',
        description: 'Wallet Topup via Bank Transfer',
        amount: 750.00,
        status: 'COMPLETED',
        createDateTime: yesterday.toISOString(),
        customerPaymentMethod: 'Bank Transfer',
        paymentSource: 'Customer Emirates NBD Account ending in 1234',
        referenceNumber: 'REF987654321',
        thaklisBankName: 'Dubai Islamic Bank',
        thaklisBankAccount: 'Thaklis - DIB Shariah Account',
        bankName: 'Dubai Islamic Bank',
        approvedBy: 'Finance Admin'
      },
      // Credit: Topup via Tabby (Today)
      {
        id: 'txn-003',
        date: now.toISOString().split('T')[0],
        type: 'CREDIT',
        description: 'Wallet Topup via Tabby',
        amount: 291.00,
        status: 'COMPLETED',
        createDateTime: now.toISOString(),
        customerPaymentMethod: 'Tabby',
        paymentSource: 'Tabby BNPL - Customer Mobile: +971xxxxxxxx',
        transactionId: 'tabby_mock789',
        gatewayFee: 9.00,
        grossAmount: 300.00
      },
      // Debit: Payment for Trade License Renewal (2 days ago)
      {
        id: 'txn-004',
        date: twoDaysAgo.toISOString().split('T')[0],
        type: 'DEBIT',
        description: 'Payment for Trade License Renewal (TKT-2024-001)',
        amount: -122.00,
        status: 'COMPLETED',
        createDateTime: twoDaysAgo.toISOString(),
        customerPaymentMethod: 'Wallet',
        ticketNumber: 'TKT-2024-001',
        serviceName: 'Trade License Renewal',
        paymentDistribution: {
          governmentFees: 100.00,
          serviceCharge: 20.00,
          tax: 2.00,
          servicePartner: 'Dubai Economic Department',
          channelPartnerCommission: 4.00, // 20% of service charge
          thaklisFee: 16.00
        }
      },
      // Debit: Payment for Visa Renewal (3 days ago)
      {
        id: 'txn-005',
        date: threeDaysAgo.toISOString().split('T')[0],
        type: 'DEBIT',
        description: 'Payment for Visa Renewal (TKT-2024-002)',
        amount: -350.00,
        status: 'COMPLETED',
        createDateTime: threeDaysAgo.toISOString(),
        customerPaymentMethod: 'Wallet',
        ticketNumber: 'TKT-2024-002',
        serviceName: 'Visa Renewal',
        paymentDistribution: {
          governmentFees: 300.00,
          serviceCharge: 45.00,
          tax: 5.00,
          servicePartner: 'GDRFA Dubai',
          channelPartnerCommission: 9.00, // 20% of service charge
          thaklisFee: 36.00
        }
      }
    ],
    topupRequests: [
      // Completed gateway payment
      {
        id: 'topup-001',
        requestedAt: threeDaysAgo.toISOString(),
        requestedBy: 'Mock User',
        requestedByEmail: 'user@example.com',
        amount: 500.00,
        currency: 'AED',
        status: 'COMPLETED',
        paymentMethod: 'GATEWAY',
        gatewayName: 'Stripe',
        gatewayType: 'CARD',
        transactionId: 'ch_mock123456',
        gatewayFee: 14.50,
        netAmount: 485.50,
        processedAt: threeDaysAgo.toISOString(),
        walletBalanceBefore: 3041.00,
        walletBalanceAfter: 3526.50,
        customerPaymentMethod: 'Stripe',
        paymentSource: 'Customer Credit Card ending in 4242',
        cardBrand: 'Visa',
        customerBankName: 'Emirates NBD',
        thaklisBankAccount: 'N/A (Gateway Payment)' // Gateway payments don't use Thaklis bank accounts
      },
      // Pending offline bank transfer
      {
        id: 'topup-002',
        requestedAt: twoDaysAgo.toISOString(),
        requestedBy: 'Mock User',
        requestedByEmail: 'user@example.com',
        amount: 1000.00,
        currency: 'AED',
        status: 'PENDING_APPROVAL',
        paymentMethod: 'BANK_TRANSFER',
        gatewayName: null,
        gatewayType: null,
        bankName: 'Emirates NBD',
        referenceNumber: 'REF123456789',
        bankAccountName: 'Thaklis - ENBD Main Account',
        depositSlip: 'receipt_001.jpg',
        approvedBy: null,
        approvedAt: null,
        gatewayFee: 0,
        netAmount: 1000.00,
        processedAt: null,
        walletBalanceBefore: 3526.50,
        walletBalanceAfter: 3526.50,
        customerPaymentMethod: 'Bank Transfer',
        customerNotes: 'Transferred from my Emirates NBD account ending in 1234',
        paymentSource: 'Customer Emirates NBD Account - Account ending in 1234',
        customerBankName: 'Emirates NBD',
        thaklisBankAccount: 'Thaklis - ENBD Main Account'
      },
      // Completed offline payment
      {
        id: 'topup-003',
        requestedAt: yesterday.toISOString(),
        requestedBy: 'Mock User',
        requestedByEmail: 'user@example.com',
        amount: 750.00,
        currency: 'AED',
        status: 'COMPLETED',
        paymentMethod: 'BANK_TRANSFER',
        gatewayName: null,
        gatewayType: null,
        bankName: 'Dubai Islamic Bank',
        referenceNumber: 'REF987654321',
        bankAccountName: 'Thaklis - DIB Shariah Account',
        depositSlip: 'receipt_002.jpg',
        approvedBy: 'Finance Admin',
        approvedAt: yesterday.toISOString(),
        gatewayFee: 0,
        netAmount: 750.00,
        processedAt: yesterday.toISOString(),
        walletBalanceBefore: 2776.50,
        walletBalanceAfter: 3526.50,
        customerPaymentMethod: 'Bank Transfer',
        customerNotes: '',
        paymentSource: 'Customer Emirates NBD Account - Account ending in 1234',
        customerBankName: 'Emirates NBD',
        thaklisBankAccount: 'Thaklis - DIB Shariah Account'
      },
      // Recent gateway payment
      {
        id: 'topup-004',
        requestedAt: now.toISOString(),
        requestedBy: 'Mock User',
        requestedByEmail: 'user@example.com',
        amount: 300.00,
        currency: 'AED',
        status: 'COMPLETED',
        paymentMethod: 'GATEWAY',
        gatewayName: 'Tabby',
        gatewayType: 'BNPL',
        transactionId: 'tabby_mock789',
        gatewayFee: 9.00,
        netAmount: 291.00,
        processedAt: now.toISOString(),
        walletBalanceBefore: 3235.50,
        walletBalanceAfter: 3526.50,
        customerPaymentMethod: 'Tabby',
        paymentSource: 'Tabby Buy Now Pay Later - Mobile: +971xxxxxxxx',
        customerBankName: 'Tabby',
        thaklisBankAccount: 'N/A (Gateway Payment)'
      }
    ],
    ticketMappings: [
      {
        id: 'ticket-004',
        orgTicketNumber: 'TKT-2024-004',
        serviceName: 'Emirates ID Renewal',
        totalCost: 200.00,
        governmentFees: 170.00,
        serviceCharge: 25.00,
        tax: 5.00,
        status: 'COMPLETED',
        paymentStatus: 'UNPAID',
        completedDate: fiveDaysAgo.toISOString(),
        createdDate: fiveDaysAgo.toISOString(),
        servicePartner: 'Federal Authority for Identity and Citizenship',
        channelPartnerCommission: 5.00, // 20% of service charge
        expectedThaklisFee: 20.00
      },
      {
        id: 'ticket-005',
        orgTicketNumber: 'TKT-2024-005',
        serviceName: 'Establishment Card',
        totalCost: 150.00,
        governmentFees: 120.00,
        serviceCharge: 28.00,
        tax: 2.00,
        status: 'COMPLETED',
        paymentStatus: 'UNPAID',
        completedDate: fourDaysAgo.toISOString(),
        createdDate: fourDaysAgo.toISOString(),
        servicePartner: 'Ministry of Human Resources',
        channelPartnerCommission: 5.60, // 20% of service charge
        expectedThaklisFee: 22.40
      },
      {
        id: 'ticket-006',
        orgTicketNumber: 'TKT-2024-006',
        serviceName: 'Commercial License Amendment',
        totalCost: 450.00,
        governmentFees: 400.00,
        serviceCharge: 45.00,
        tax: 5.00,
        status: 'COMPLETED',
        paymentStatus: 'UNPAID',
        completedDate: threeDaysAgo.toISOString(),
        createdDate: threeDaysAgo.toISOString(),
        servicePartner: 'Dubai Economy',
        channelPartnerCommission: 9.00, // 20% of service charge
        expectedThaklisFee: 36.00
      }
    ],
    partnerPayments: [
      {
        id: 'payment-001',
        paymentDate: twoDaysAgo.toISOString(),
        ticketNumber: 'TKT-2024-001', // Keep for backward compatibility
        serviceName: 'Trade License Renewal', // Keep for backward compatibility
        totalAmount: 122.00,
        verificationStatus: 'VERIFIED', // Mark as verified
        isVerified: true,
        verifiedByPartner: true,
        verifiedDate: twoDaysAgo.toISOString(),
        tickets: [
          {
            ticketNumber: 'TKT-2024-001',
            serviceName: 'Trade License Renewal',
            amount: 122.00,
            governmentFees: 100.00,
            serviceCharge: 20.00,
            tax: 2.00
          }
        ],
        payments: [
          {
            recipient: 'Dubai Economic Department',
            recipientType: 'SERVICE_PARTNER',
            amount: 120.00, // govt fees + service charge
            description: 'Government Fees (100) + Service Charge (20)',
            status: 'PAID',
            paidAt: twoDaysAgo.toISOString()
          },
          {
            recipient: 'Premium Business Services LLC',
            recipientType: 'CHANNEL_PARTNER',
            amount: 4.00, // 20% of service charge
            description: 'Channel Partner Commission (20% of service charge)',
            status: 'PAID',
            paidAt: twoDaysAgo.toISOString()
          },
          {
            recipient: 'Thaklis',
            recipientType: 'THAKLIS',
            amount: 16.00, // Remaining 80% of service charge
            description: 'Thaklis Service Fee',
            status: 'RETAINED',
            paidAt: twoDaysAgo.toISOString()
          }
        ]
      },
      {
        id: 'payment-002',
        paymentDate: threeDaysAgo.toISOString(),
        ticketNumber: 'TKT-2024-002, TKT-2024-003, TKT-2024-004', // Multiple tickets
        serviceName: 'Multiple Services',
        totalAmount: 672.00, // 350 + 122 + 200
        verificationStatus: 'PENDING', // Mark as pending verification
        isVerified: false,
        verifiedByPartner: false,
        tickets: [
          {
            ticketNumber: 'TKT-2024-002',
            serviceName: 'Visa Renewal',
            amount: 350.00,
            governmentFees: 300.00,
            serviceCharge: 45.00,
            tax: 5.00
          },
          {
            ticketNumber: 'TKT-2024-003',
            serviceName: 'Trade License Amendment',
            amount: 122.00,
            governmentFees: 100.00,
            serviceCharge: 20.00,
            tax: 2.00
          },
          {
            ticketNumber: 'TKT-2024-004',
            serviceName: 'Emirates ID Renewal',
            amount: 200.00,
            governmentFees: 170.00,
            serviceCharge: 25.00,
            tax: 5.00
          }
        ],
        payments: [
          {
            recipient: 'Government Entities',
            recipientType: 'SERVICE_PARTNER',
            amount: 660.00, // Total govt fees + service charges
            description: 'Government Fees (570) + Service Charges (90)',
            status: 'PAID',
            paidAt: threeDaysAgo.toISOString()
          },
          {
            recipient: 'Golden Visa Services',
            recipientType: 'CHANNEL_PARTNER',
            amount: 18.00, // 20% of total service charges (90)
            description: 'Channel Partner Commission (20% of service charges)',
            status: 'PAID',
            paidAt: threeDaysAgo.toISOString()
          },
          {
            recipient: 'Thaklis',
            recipientType: 'THAKLIS',
            amount: 72.00, // 80% of total service charges (90)
            description: 'Thaklis Service Fee',
            status: 'RETAINED',
            paidAt: threeDaysAgo.toISOString()
          }
        ]
      }
    ]
  };
};

// Get mock data from localStorage
export const getMockWalletData = () => {
  try {
    const data = localStorage.getItem(MOCK_DATA_KEY);
    if (data) {
      return JSON.parse(data);
    }
    return initializeMockData();
  } catch (error) {
    console.error('Error reading mock wallet data:', error);
    return initializeMockData();
  }
};

// Save mock data to localStorage
export const saveMockWalletData = (data) => {
  try {
    localStorage.setItem(MOCK_DATA_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving mock wallet data:', error);
    return false;
  }
};

// Enable mock data mode
export const enableMockData = () => {
  const data = getMockWalletData();
  data.enabled = true;
  saveMockWalletData(data);
};

// Disable mock data mode
export const disableMockData = () => {
  const data = getMockWalletData();
  data.enabled = false;
  saveMockWalletData(data);
};

// Check if mock data is enabled
export const isMockDataEnabled = () => {
  const data = getMockWalletData();
  return data.enabled === true;
};

// Clear all mock data
export const clearMockData = () => {
  localStorage.removeItem(MOCK_DATA_KEY);
};

// Reset mock data to initial state
export const resetMockData = () => {
  const freshData = initializeMockData();
  saveMockWalletData(freshData);
  console.log('Mock data reset successfully. Please refresh the page.');
  return freshData;
};

// Add a topup request (gateway payment - instant)
export const addGatewayTopupRequest = (formData) => {
  const data = getMockWalletData();
  const amount = parseFloat(formData.amount);
  const fee = formData.gatewayFee || 0;
  const netAmount = formData.netAmount || amount;

  const request = {
    id: `topup-${Date.now()}`,
    requestedAt: new Date().toISOString(),
    requestedBy: 'Mock User',
    requestedByEmail: 'user@example.com',
    amount: amount,
    currency: 'AED',
    status: 'COMPLETED',
    paymentMethod: 'GATEWAY',
    gatewayName: formData.selectedGateway?.gatewayName || 'Stripe',
    gatewayType: formData.selectedGateway?.gatewayType || 'CARD',
    transactionId: `ch_${Math.random().toString(36).substr(2, 9)}`,
    gatewayFee: fee,
    netAmount: netAmount,
    processedAt: new Date().toISOString(),
    walletBalanceBefore: data.balance,
    walletBalanceAfter: data.balance + netAmount,
    customerPaymentMethod: formData.selectedGateway?.gatewayName || 'Online Payment',
    paymentSource: `${formData.selectedGateway?.gatewayName} Payment Gateway`,
    thaklisBankAccount: 'N/A (Gateway Payment)'
  };

  // Add transaction
  const transaction = {
    id: `txn-${Date.now()}`,
    date: new Date().toISOString().split('T')[0],
    type: 'CREDIT',
    description: `Wallet Topup via ${request.gatewayName}`,
    amount: netAmount,
    status: 'COMPLETED',
    createDateTime: new Date().toISOString(),
    customerPaymentMethod: request.gatewayName,
    transactionId: request.transactionId,
    gatewayFee: fee,
    grossAmount: amount,
    paymentSource: `${request.gatewayName} Payment Gateway`,
    thaklisBankAccount: 'N/A (Gateway Payment)'
  };

  // Update balance
  data.balance += netAmount;
  data.topupRequests.unshift(request);
  data.transactions.unshift(transaction);

  saveMockWalletData(data);
  return request;
};

// Add a topup request (offline payment - pending approval)
export const addOfflineTopupRequest = (formData) => {
  const data = getMockWalletData();
  const amount = parseFloat(formData.amount);

  const request = {
    id: `topup-${Date.now()}`,
    requestedAt: new Date().toISOString(),
    requestedBy: 'Mock User',
    requestedByEmail: 'user@example.com',
    amount: amount,
    currency: 'AED',
    status: 'PENDING_APPROVAL', // Key difference - pending approval
    paymentMethod: 'BANK_TRANSFER',
    gatewayName: null,
    gatewayType: null,
    bankName: formData.selectedBankAccount?.bankName || 'Emirates NBD',
    referenceNumber: formData.referenceNumber || 'REF123456',
    bankAccountName: formData.selectedBankAccount?.accountName || 'Thaklis - ENBD Main Account',
    depositSlip: formData.receipt?.name || 'receipt.jpg',
    approvedBy: null,
    approvedAt: null,
    gatewayFee: 0,
    netAmount: amount,
    processedAt: null,
    walletBalanceBefore: data.balance,
    walletBalanceAfter: data.balance, // No change until approved
    customerPaymentMethod: formData.paymentMethod === 'CASH_DEPOSIT' ? 'Cash Deposit' : 'Bank Transfer',
    customerNotes: formData.customerNotes || ''
  };

  data.topupRequests.unshift(request);
  saveMockWalletData(data);
  return request;
};

// Approve a pending topup request
export const approveTopupRequest = (requestId) => {
  const data = getMockWalletData();
  const request = data.topupRequests.find(r => r.id === requestId);

  if (request && request.status === 'PENDING_APPROVAL') {
    request.status = 'COMPLETED';
    request.approvedBy = 'Finance Admin';
    request.approvedAt = new Date().toISOString();
    request.processedAt = new Date().toISOString();
    request.walletBalanceAfter = data.balance + request.netAmount;

    // Add transaction
    const transaction = {
      id: `txn-${Date.now()}`,
      date: new Date().toISOString().split('T')[0],
      type: 'CREDIT',
      description: `Wallet Topup via ${request.paymentMethod}`,
      amount: request.netAmount,
      status: 'COMPLETED',
      createDateTime: new Date().toISOString(),
      customerPaymentMethod: request.customerPaymentMethod,
      paymentSource: request.paymentSource || 'Customer Bank Account',
      referenceNumber: request.referenceNumber,
      thaklisBankAccount: request.bankAccountName || request.thaklisBankAccount,
      thaklisBankName: request.bankName
    };

    // Update balance
    data.balance += request.netAmount;
    data.transactions.unshift(transaction);

    saveMockWalletData(data);
    return request;
  }

  return null;
};

// Add mock completed tickets for ticket mapping
export const addMockCompletedTickets = () => {
  const data = getMockWalletData();

  const tickets = [
    {
      id: 'ticket-001',
      orgTicketNumber: 'TKT-2024-001',
      serviceName: 'Trade License Renewal',
      totalCost: 122.00,
      governmentFees: 100.00,
      serviceCharge: 20.00,
      tax: 2.00,
      servicePartner: 'Dubai Economic Department',
      partnerServiceFee: 12.00,  // 60% of service charge (partner's portion)
      partnerPayment: 112.00,  // Govt fees (100) + Partner service fee (12)
      channelPartnerCommission: 2.40,  // 12% of service charge (20% of partner's 60%)
      channelPartnerName: 'Al Sada Partners',
      expectedThaklisFee: 5.60,  // 28% of service charge (remaining after partner 60% and channel 12%)
      status: 'COMPLETED',
      paymentStatus: 'UNPAID',
      createdDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'ticket-002',
      orgTicketNumber: 'TKT-2024-002',
      serviceName: 'Visa Renewal',
      totalCost: 350.00,
      governmentFees: 300.00,
      serviceCharge: 40.00,
      tax: 10.00,
      servicePartner: 'General Directorate of Residency',
      partnerServiceFee: 24.00,  // 60% of service charge
      partnerPayment: 324.00,  // Govt fees (300) + Partner service fee (24)
      channelPartnerCommission: 4.80,  // 12% of service charge
      channelPartnerName: 'Al Sada Partners',
      expectedThaklisFee: 11.20,  // 28% of service charge
      status: 'COMPLETED',
      paymentStatus: 'UNPAID',
      createdDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'ticket-003',
      orgTicketNumber: 'TKT-2024-003',
      serviceName: 'Emirates ID Renewal',
      totalCost: 200.00,
      governmentFees: 170.00,
      serviceCharge: 25.00,
      tax: 5.00,
      servicePartner: 'Federal Authority for Identity',
      partnerServiceFee: 15.00,  // 60% of service charge
      partnerPayment: 185.00,  // Govt fees (170) + Partner service fee (15)
      channelPartnerCommission: 3.00,  // 12% of service charge
      channelPartnerName: 'Al Sada Partners',
      expectedThaklisFee: 7.00,  // 28% of service charge
      status: 'COMPLETED',
      paymentStatus: 'UNPAID',
      createdDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
    }
  ];

  data.ticketMappings = tickets;
  saveMockWalletData(data);
  return tickets;
};

// Pay for tickets via ticket mapping
export const payForTickets = (ticketIds) => {
  const data = getMockWalletData();
  const tickets = data.ticketMappings.filter(t => ticketIds.includes(t.id));
  const totalAmount = tickets.reduce((sum, t) => sum + t.totalCost, 0);

  if (totalAmount > data.balance) {
    throw new Error('Insufficient balance');
  }

  // Mark tickets as paid
  data.ticketMappings = data.ticketMappings.map(t => {
    if (ticketIds.includes(t.id)) {
      return { ...t, paymentStatus: 'PAID', paidAt: new Date().toISOString() };
    }
    return t;
  });

  // Add transaction
  const transaction = {
    id: `txn-${Date.now()}`,
    date: new Date().toISOString().split('T')[0],
    type: 'DEBIT',
    description: `Payment for ${tickets.length} ticket(s)`,
    amount: -totalAmount,
    status: 'COMPLETED',
    createDateTime: new Date().toISOString(),
    customerPaymentMethod: 'Wallet',
    tickets: tickets.map(t => t.orgTicketNumber).join(', ')
  };

  // Update balance
  data.balance -= totalAmount;
  data.transactions.unshift(transaction);

  saveMockWalletData(data);
  return { tickets, transaction };
};

// Get current balance
export const getMockBalance = () => {
  const data = getMockWalletData();
  return data.balance;
};

// Get mock transactions
export const getMockTransactions = () => {
  const now = new Date();
  const daysAgo = (days) => new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  return [
    // TOPUP Transactions (Money IN)
    {
      id: 'txn-topup-001',
      type: 'TOPUP',
      date: daysAgo(10).toISOString(),
      amount: 5000.00,
      status: 'COMPLETED',
      paymentMethod: 'OFFLINE',
      from: {
        name: 'ABC Corporation',
        type: 'CUSTOMER'
      },
      to: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      referenceNumber: 'TXN-10-001',
      notes: 'Customer wallet top-up via bank transfer'
    },
    {
      id: 'txn-topup-002',
      type: 'TOPUP',
      date: daysAgo(7).toISOString(),
      amount: 3000.00,
      status: 'COMPLETED',
      paymentMethod: 'ONLINE',
      from: {
        name: 'Tech Solutions LLC',
        type: 'CUSTOMER'
      },
      to: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      referenceNumber: 'TXN-07-001',
      notes: 'Customer wallet top-up via credit card'
    },
    {
      id: 'txn-topup-003',
      type: 'TOPUP',
      date: daysAgo(5).toISOString(),
      amount: 2500.00,
      status: 'COMPLETED',
      paymentMethod: 'OFFLINE',
      from: {
        name: 'Emirates Services',
        type: 'CUSTOMER'
      },
      to: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      referenceNumber: 'TXN-05-001',
      notes: 'Customer wallet top-up via bank transfer'
    },
    {
      id: 'txn-topup-004',
      type: 'TOPUP',
      date: daysAgo(3).toISOString(),
      amount: 1500.00,
      status: 'COMPLETED',
      paymentMethod: 'ONLINE',
      from: {
        name: 'Global Enterprises',
        type: 'CUSTOMER'
      },
      to: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      referenceNumber: 'TXN-03-001',
      notes: 'Customer wallet top-up via bank transfer'
    },

    // PARTNER_PAYMENT Transactions (Money OUT)
    {
      id: 'txn-partner-001',
      type: 'PARTNER_PAYMENT',
      date: daysAgo(8).toISOString(),
      amount: 1250.00,
      status: 'COMPLETED',
      paymentMethod: 'ONLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Emirates Services LLC',
        type: 'SERVICE_PARTNER'
      },
      sourceBank: 'Dubai Islamic Bank',
      destinationBank: 'Emirates NBD',
      referenceNumber: 'PAY-08-001',
      notes: 'Service partner payment for completed services',
      tickets: [
        {
          ticketNumber: 'TKT-2024-001',
          serviceName: 'Trade License Renewal',
          amount: 1250.00
        }
      ]
    },
    {
      id: 'txn-partner-002',
      type: 'PARTNER_PAYMENT',
      date: daysAgo(6).toISOString(),
      amount: 850.00,
      status: 'COMPLETED',
      paymentMethod: 'OFFLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Dubai Consultants',
        type: 'SERVICE_PARTNER'
      },
      sourceBank: 'Abu Dhabi Commercial Bank',
      destinationBank: 'Dubai Islamic Bank',
      referenceNumber: 'PAY-06-001',
      notes: 'Service partner payment for completed services',
      tickets: [
        {
          ticketNumber: 'TKT-2024-002',
          serviceName: 'Visa Renewal',
          amount: 850.00
        }
      ]
    },
    {
      id: 'txn-partner-003',
      type: 'PARTNER_PAYMENT',
      date: daysAgo(4).toISOString(),
      amount: 1050.00,
      status: 'COMPLETED',
      paymentMethod: 'ONLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Federal Authority for Identity & Citizenship',
        type: 'SERVICE_PARTNER'
      },
      sourceBank: 'Emirates NBD',
      destinationBank: 'ADCB',
      referenceNumber: 'PAY-04-001',
      notes: 'Service partner payment for completed services',
      tickets: [
        {
          ticketNumber: 'TKT-2024-003',
          serviceName: 'Emirates ID Renewal',
          amount: 520.00
        },
        {
          ticketNumber: 'TKT-2024-004',
          serviceName: 'Establishment Card',
          amount: 530.00
        }
      ]
    },
    {
      id: 'txn-partner-004',
      type: 'PARTNER_PAYMENT',
      date: daysAgo(2).toISOString(),
      amount: 425.00,
      status: 'COMPLETED',
      paymentMethod: 'OFFLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Dubai Municipality',
        type: 'SERVICE_PARTNER'
      },
      sourceBank: 'Mashreq Bank',
      destinationBank: 'Emirates NBD',
      referenceNumber: 'PAY-02-001',
      notes: 'Service partner payment for completed services',
      tickets: [
        {
          ticketNumber: 'TKT-2024-005',
          serviceName: 'Building Permit',
          amount: 425.00
        }
      ]
    },

    // COMMISSION_PAYMENT Transactions (Money OUT)
    {
      id: 'txn-commission-001',
      type: 'COMMISSION_PAYMENT',
      date: daysAgo(9).toISOString(),
      amount: 250.00,
      status: 'COMPLETED',
      paymentMethod: 'ONLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Elite Business Consultants',
        type: 'CHANNEL_PARTNER'
      },
      sourceBank: 'Dubai Islamic Bank',
      destinationBank: 'Emirates NBD',
      referenceNumber: 'COM-09-001',
      notes: 'Monthly commission payout - September 2024',
      tickets: [
        {
          ticketNumber: 'TKT-2024-010',
          serviceName: 'Trade License Renewal',
          commission: 125.00
        },
        {
          ticketNumber: 'TKT-2024-011',
          serviceName: 'Visa Stamping',
          commission: 125.00
        }
      ]
    },
    {
      id: 'txn-commission-002',
      type: 'COMMISSION_PAYMENT',
      date: daysAgo(9).toISOString(),
      amount: 150.00,
      status: 'COMPLETED',
      paymentMethod: 'OFFLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Business Growth Partners',
        type: 'CHANNEL_PARTNER'
      },
      sourceBank: 'ADCB',
      destinationBank: 'Dubai Islamic Bank',
      referenceNumber: 'COM-09-002',
      notes: 'Monthly commission payout - September 2024',
      tickets: [
        {
          ticketNumber: 'TKT-2024-012',
          serviceName: 'Emirates ID Renewal',
          commission: 75.00
        },
        {
          ticketNumber: 'TKT-2024-013',
          serviceName: 'Establishment Card',
          commission: 75.00
        }
      ]
    },
    {
      id: 'txn-commission-003',
      type: 'COMMISSION_PAYMENT',
      date: daysAgo(9).toISOString(),
      amount: 100.00,
      status: 'COMPLETED',
      paymentMethod: 'ONLINE',
      from: {
        name: 'Thaklis',
        type: 'THAKLIS'
      },
      to: {
        name: 'Professional Services Group',
        type: 'CHANNEL_PARTNER'
      },
      sourceBank: 'Emirates NBD',
      destinationBank: 'Mashreq Bank',
      referenceNumber: 'COM-09-003',
      notes: 'Monthly commission payout - September 2024',
      tickets: [
        {
          ticketNumber: 'TKT-2024-014',
          serviceName: 'Building Permit',
          commission: 50.00
        },
        {
          ticketNumber: 'TKT-2024-015',
          serviceName: 'Commercial License',
          commission: 50.00
        }
      ]
    }
  ];
};

// Get mock topup requests
export const getMockTopupRequests = () => {
  const data = getMockWalletData();
  return data.topupRequests;
};

// Get mock unpaid tickets
export const getMockUnpaidTickets = () => {
  const data = getMockWalletData();
  return data.ticketMappings.filter(t => t.paymentStatus === 'UNPAID');
};

export const getMockPartnerPayments = () => {
  let data = getMockWalletData();
  const payments = data.partnerPayments || [];

  // Check if we need to reset data (old structure or missing payments)
  if (payments.length < 2 || !payments[1]?.tickets || payments[1]?.tickets?.length < 3) {
    console.log('⚠️ Mock data is outdated or incomplete. Resetting to fresh data...');
    data = initializeMockData();
    saveMockWalletData(data);
    return data.partnerPayments || [];
  }

  // Ensure backwards compatibility: if tickets array doesn't exist, create it from single ticket fields
  return payments.map(payment => {
    if (!payment.tickets && payment.ticketNumber) {
      // Legacy format - convert to new format with tickets array
      return {
        ...payment,
        tickets: [{
          ticketNumber: payment.ticketNumber,
          serviceName: payment.serviceName,
          amount: payment.totalAmount || payment.amount || 0,
          governmentFees: 0,
          serviceCharge: 0,
          tax: 0
        }]
      };
    }
    return payment;
  });
};

// ============================================================================
// NEW: Pending Approvals Mock Data with Reconciliation States
// ============================================================================

// 1. Customer Top-ups (Money IN - Awaiting Verification)
export const getMockPendingTopups = () => {
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
  const oneDayAgo = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000);

  return [
    {
      id: 'topup-request-001',
      type: 'CUSTOMER_TOPUP_INCOMING',
      customerId: 'user-123',
      customerName: 'Ahmed Al Mansoori',
      amount: 2000.00,
      currency: 'AED',
      status: 'PENDING_BANK_VERIFICATION',
      moneyLocation: {
        inThaklisBank: true,
        verifiedInBank: false,
        inThaklisWallet: false,
        availableToCustomer: false
      },
      requestDate: oneDayAgo.toISOString(),
      paymentMethod: 'BANK_TRANSFER',
      bankDetails: {
        bankName: 'Emirates NBD',
        accountNumber: '****5678',
        referenceNumber: 'REF-20241020-001',
        transferDate: oneDayAgo.toISOString().split('T')[0],
        expectedAmount: 2000.00,
        adminVerified: false,
        bankStatementMatch: null,
        bankStatementAttachment: null
      },
      attachments: [
        {
          id: 'att-001',
          type: 'BANK_RECEIPT',
          fileName: 'bank_transfer_receipt.pdf',
          uploadedDate: oneDayAgo.toISOString()
        }
      ],
      priority: 'HIGH',
      daysWaiting: 1,
      actions: ['VERIFY_AND_APPROVE', 'REQUEST_MORE_INFO', 'REJECT']
    },
    {
      id: 'topup-request-002',
      type: 'CUSTOMER_TOPUP_INCOMING',
      customerId: 'user-456',
      customerName: 'Sara Mohammed',
      amount: 1500.00,
      currency: 'AED',
      status: 'PENDING_BANK_VERIFICATION',
      moneyLocation: {
        inThaklisBank: true,
        verifiedInBank: false,
        inThaklisWallet: false,
        availableToCustomer: false
      },
      requestDate: threeDaysAgo.toISOString(),
      paymentMethod: 'CHECK',
      bankDetails: {
        bankName: 'Dubai Islamic Bank',
        checkNumber: 'CHK-9876543',
        expectedClearanceDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        checkImageAttached: true
      },
      priority: 'MEDIUM',
      daysWaiting: 3,
      actions: ['VERIFY_AND_APPROVE', 'REJECT']
    },
    {
      id: 'topup-request-003',
      type: 'CUSTOMER_TOPUP_INCOMING',
      customerId: 'user-789',
      customerName: 'Mohammed Al Fahim',
      amount: 1500.00,
      currency: 'AED',
      status: 'PENDING_BANK_VERIFICATION',
      moneyLocation: {
        inThaklisBank: true,
        verifiedInBank: false,
        inThaklisWallet: false,
        availableToCustomer: false
      },
      requestDate: twoDaysAgo.toISOString(),
      paymentMethod: 'BANK_TRANSFER',
      bankDetails: {
        bankName: 'Abu Dhabi Commercial Bank',
        referenceNumber: 'REF-20241019-088',
        transferDate: twoDaysAgo.toISOString().split('T')[0]
      },
      priority: 'MEDIUM',
      daysWaiting: 2,
      actions: ['VERIFY_AND_APPROVE', 'REJECT']
    }
  ];
};

// 2. Partner Payments (Money OUT - Awaiting Approval)
export const getMockPendingPartnerPayments = () => {
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);

  return [
    {
      id: 'partner-payment-001',
      type: 'SERVICE_PARTNER_PAYMENT_OUTGOING',
      partnerId: 'partner-001',
      partnerName: 'Dubai Municipality Typing',
      amount: 185.00,
      currency: 'AED',
      status: 'PENDING_APPROVAL',
      moneyLocation: {
        inThaklisBank: true,
        inThaklisWallet: true,
        approvedForPayment: false,
        sentToPartner: false
      },
      relatedTickets: [
        {
          ticketId: 'TKT-2024-015',
          ticketNumber: 'TKT-2024-015',
          serviceName: 'Trade License Renewal',
          customerPayment: 200.00,
          partnerPayment: 185.00,
          thaklisCommission: 15.00,
          completedDate: threeDaysAgo.toISOString()
        }
      ],
      breakdown: {
        numberOfTickets: 1,
        totalCustomerPayments: 200.00,
        totalPartnerPayments: 185.00,
        totalThaklisCommission: 15.00
      },
      requestDate: threeDaysAgo.toISOString(),
      daysWaiting: 3,
      partnerBankDetails: {
        bankName: 'Emirates Islamic Bank',
        accountNumber: '****1234',
        accountName: 'Dubai Municipality Typing Center LLC',
        iban: 'AE07****1234'
      },
      approval: {
        requestedBy: 'system',
        requiredApprovers: ['finance_manager'],
        currentApprover: null,
        approvalDeadline: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      },
      priority: 'MEDIUM',
      actions: ['APPROVE', 'REJECT', 'REQUEST_MORE_INFO']
    },
    {
      id: 'partner-payment-002',
      type: 'SERVICE_PARTNER_PAYMENT_OUTGOING',
      partnerId: 'partner-003',
      partnerName: 'Al Marjan Typing Services',
      amount: 133.20,
      currency: 'AED',
      status: 'PENDING_APPROVAL',
      moneyLocation: {
        inThaklisBank: true,
        inThaklisWallet: true,
        approvedForPayment: false,
        sentToPartner: false
      },
      relatedTickets: [
        {
          ticketId: 'TKT-2024-018',
          serviceName: 'Visa Stamping',
          customerPayment: 80.00,
          partnerPayment: 72.00,
          thaklisCommission: 8.00
        },
        {
          ticketId: 'TKT-2024-019',
          serviceName: 'Emirates ID Application',
          customerPayment: 68.00,
          partnerPayment: 61.20,
          thaklisCommission: 6.80
        }
      ],
      breakdown: {
        numberOfTickets: 2,
        totalCustomerPayments: 148.00,
        totalPartnerPayments: 133.20,
        totalThaklisCommission: 14.80
      },
      requestDate: twoDaysAgo.toISOString(),
      daysWaiting: 2,
      partnerBankDetails: {
        bankName: 'Mashreq Bank',
        accountNumber: '****7890',
        accountName: 'Al Marjan Typing Services',
        iban: 'AE09****7890'
      },
      approval: {
        requestedBy: 'system',
        requiredApprovers: ['finance_manager'],
        approvalDeadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      },
      priority: 'MEDIUM',
      actions: ['APPROVE', 'REJECT', 'REQUEST_MORE_INFO']
    }
  ];
};

// 3. Approved Awaiting Transfer (Money OUT - Awaiting Bank Transfer)
export const getMockApprovedAwaitingTransfer = () => {
  const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);
  const fourDaysAgo = new Date(Date.now() - 4 * 24 * 60 * 60 * 1000);

  return [
    {
      id: 'partner-payment-004',
      type: 'SERVICE_PARTNER_PAYMENT_OUTGOING',
      partnerId: 'partner-004',
      partnerName: 'Emarat Typing Center',
      amount: 324.00,
      currency: 'AED',
      status: 'APPROVED_AWAITING_BANK_TRANSFER',
      moneyLocation: {
        inThaklisBank: true,
        inThaklisWallet: true,
        approvedForPayment: true,
        sentToPartner: false,
        readyForBankTransfer: true
      },
      relatedTickets: [
        {
          ticketId: 'TKT-2024-012',
          serviceName: 'Commercial License Renewal',
          customerPayment: 360.00,
          partnerPayment: 324.00,
          thaklisCommission: 36.00,
          completedDate: fiveDaysAgo.toISOString()
        }
      ],
      breakdown: {
        numberOfTickets: 1,
        totalCustomerPayments: 360.00,
        totalPartnerPayments: 324.00,
        totalThaklisCommission: 36.00
      },
      requestDate: fiveDaysAgo.toISOString(),
      approvalDate: fourDaysAgo.toISOString(),
      daysWaitingForTransfer: 5,
      approval: {
        approvedBy: 'Fatima Al Hashmi',
        approvedByRole: 'Finance Manager',
        approvalDate: fourDaysAgo.toISOString(),
        approvalNotes: 'Approved - verified ticket completion and partner details'
      },
      partnerBankDetails: {
        bankName: 'ADCB',
        accountNumber: '****4567',
        accountName: 'Emarat Typing Center',
        iban: 'AE12****4567',
        swiftCode: 'ADCBAEAA'
      },
      nextAction: {
        action: 'RECORD_BANK_TRANSFER',
        description: 'Perform bank transfer and record transaction details',
        assignedTo: 'finance_admin',
        deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      },
      priority: 'HIGH',
      actions: ['RECORD_BANK_TRANSFER', 'CANCEL_APPROVAL']
    }
  ];
};

// 4. In Transit Payments (Money OUT - Awaiting Partner Confirmation) ⚠️ NEW
export const getMockInTransitPayments = () => {
  const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
  const fourDaysAgo = new Date(Date.now() - 4 * 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  return [
    {
      id: 'partner-payment-005',
      type: 'SERVICE_PARTNER_PAYMENT_OUTGOING',
      partnerId: 'partner-005',
      partnerName: 'Dubai Municipality',
      amount: 425.00,
      currency: 'AED',
      status: 'AWAITING_PARTNER_CONFIRMATION',
      moneyLocation: {
        inThaklisBank: false,
        inThaklisWallet: false,
        approvedForPayment: true,
        sentToPartner: true,
        receivedByPartner: null
      },
      relatedTickets: [
        {
          ticketId: 'TKT-2024-010',
          serviceName: 'Building Permit',
          customerPayment: 450.00,
          partnerPayment: 425.00,
          thaklisCommission: 25.00
        }
      ],
      breakdown: {
        numberOfTickets: 1,
        totalCustomerPayments: 450.00,
        totalPartnerPayments: 425.00,
        totalThaklisCommission: 25.00
      },
      bankTransfer: {
        transferredBy: 'Ali Mohammed',
        transferredByRole: 'Finance Admin',
        transferDate: twoDaysAgo.toISOString(),
        transferMethod: 'ONLINE_BANKING',
        bankName: 'Mashreq Bank',
        referenceNumber: 'TXN-' + twoDaysAgo.toISOString().split('T')[0].replace(/-/g, '') + '-012',
        thaklisBankDebited: true,
        debitDate: twoDaysAgo.toISOString(),
        debitAmount: 425.00,
        bankStatementMatch: true,
        bankStatementDate: twoDaysAgo.toISOString().split('T')[0]
      },
      partnerBankDetails: {
        bankName: 'Mashreq Bank',
        accountNumber: '****9012',
        accountName: 'Dubai Municipality',
        iban: 'AE15****9012'
      },
      reconciliation: {
        thaklisBankDebited: true,
        thaklisBankVerifiedDate: twoDaysAgo.toISOString(),
        thaklisBankStatementMatch: true,
        partnerBankCredited: null,
        partnerConfirmedReceipt: false,
        partnerConfirmationDate: null,
        daysSincePayment: 2,
        businessDaysSincePayment: 2,
        expectedConfirmationDate: new Date().toISOString().split('T')[0],
        autoConfirmDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        daysUntilAutoConfirm: 5,
        reconciliationStatus: 'NORMAL',
        priority: 'NORMAL',
        alerts: []
      },
      actions: ['CONFIRM_EARLY', 'VIEW_BANK_STATEMENT', 'CONTACT_PARTNER'],
      priority: 'NORMAL'
    },
    {
      id: 'partner-payment-006',
      type: 'SERVICE_PARTNER_PAYMENT_OUTGOING',
      partnerId: 'partner-006',
      partnerName: 'Federal Authority for Identity & Citizenship',
      amount: 185.00,
      currency: 'AED',
      status: 'AWAITING_PARTNER_CONFIRMATION',
      moneyLocation: {
        inThaklisBank: false,
        inThaklisWallet: false,
        approvedForPayment: true,
        sentToPartner: true,
        receivedByPartner: null
      },
      relatedTickets: [
        {
          ticketId: 'TKT-2024-008',
          serviceName: 'Emirates ID Renewal',
          customerPayment: 200.00,
          partnerPayment: 185.00,
          thaklisCommission: 15.00
        }
      ],
      breakdown: {
        numberOfTickets: 1,
        totalCustomerPayments: 200.00,
        totalPartnerPayments: 185.00,
        totalThaklisCommission: 15.00
      },
      bankTransfer: {
        transferredBy: 'Fatima Al Hashmi',
        transferDate: fourDaysAgo.toISOString(),
        transferMethod: 'ONLINE_BANKING',
        bankName: 'ADCB',
        referenceNumber: 'TXN-' + fourDaysAgo.toISOString().split('T')[0].replace(/-/g, '') + '-007',
        thaklisBankDebited: true,
        debitDate: fourDaysAgo.toISOString(),
        bankStatementMatch: true
      },
      partnerBankDetails: {
        bankName: 'ADCB',
        accountNumber: '****3456',
        accountName: 'Federal Authority for Identity',
        iban: 'AE18****3456'
      },
      reconciliation: {
        thaklisBankDebited: true,
        thaklisBankVerifiedDate: fourDaysAgo.toISOString(),
        thaklisBankStatementMatch: true,
        partnerBankCredited: null,
        partnerConfirmedReceipt: false,
        daysSincePayment: 4,
        businessDaysSincePayment: 4,
        autoConfirmDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        daysUntilAutoConfirm: 3,
        reconciliationStatus: 'FOLLOW_UP_RECOMMENDED',
        priority: 'MEDIUM',
        alerts: [
          {
            type: 'INFO',
            message: 'Payment sent 4 business days ago - consider sending follow-up reminder',
            severity: 'MEDIUM'
          }
        ],
        notes: [
          {
            date: fourDaysAgo.toISOString(),
            author: 'Fatima Al Hashmi',
            note: 'Payment initiated via ADCB online banking'
          }
        ]
      },
      actions: ['SEND_REMINDER', 'CONFIRM_EARLY', 'VIEW_BANK_STATEMENT'],
      priority: 'MEDIUM'
    },
    {
      id: 'partner-payment-007',
      type: 'SERVICE_PARTNER_PAYMENT_OUTGOING',
      partnerId: 'partner-002',
      partnerName: 'Al Sada Typing Center',
      amount: 282.50,
      currency: 'AED',
      status: 'AWAITING_PARTNER_CONFIRMATION',
      moneyLocation: {
        inThaklisBank: false,
        inThaklisWallet: false,
        sentToPartner: true,
        receivedByPartner: null
      },
      relatedTickets: [
        {
          ticketId: 'TKT-2024-005',
          serviceName: 'Establishment Card',
          customerPayment: 150.00,
          partnerPayment: 136.80,
          thaklisCommission: 13.20
        },
        {
          ticketId: 'TKT-2024-006',
          serviceName: 'Labour Contract',
          customerPayment: 160.00,
          partnerPayment: 145.70,
          thaklisCommission: 14.30
        }
      ],
      breakdown: {
        numberOfTickets: 2,
        totalCustomerPayments: 310.00,
        totalPartnerPayments: 282.50,
        totalThaklisCommission: 27.50
      },
      bankTransfer: {
        transferredBy: 'Ali Mohammed',
        transferDate: sevenDaysAgo.toISOString(),
        transferMethod: 'ONLINE_BANKING',
        bankName: 'Emirates NBD',
        referenceNumber: 'TXN-' + sevenDaysAgo.toISOString().split('T')[0].replace(/-/g, '') + '-003',
        thaklisBankDebited: true,
        debitDate: sevenDaysAgo.toISOString(),
        bankStatementMatch: true,
        bankStatementAttachment: 'statement_oct14.pdf'
      },
      partnerBankDetails: {
        bankName: 'Emirates NBD',
        accountNumber: '****6789',
        accountName: 'Al Sada Typing Center',
        iban: 'AE21****6789'
      },
      reconciliation: {
        thaklisBankDebited: true,
        thaklisBankVerifiedDate: sevenDaysAgo.toISOString(),
        thaklisBankStatementMatch: true,
        partnerBankCredited: null,
        partnerConfirmedReceipt: false,
        daysSincePayment: 7,
        businessDaysSincePayment: 7,
        autoConfirmDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        daysUntilAutoConfirm: -2,
        reconciliationStatus: 'OVERDUE',
        priority: 'HIGH',
        alerts: [
          {
            type: 'ERROR',
            message: 'OVERDUE: Payment sent 7 business days ago with no confirmation',
            severity: 'HIGH',
            actionRequired: true
          },
          {
            type: 'WARNING',
            message: 'Auto-confirmation deadline passed - investigation required',
            severity: 'HIGH'
          },
          {
            type: 'INFO',
            message: 'Partner has not responded to follow-up attempts',
            severity: 'MEDIUM'
          }
        ],
        followUpHistory: [
          {
            date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            method: 'EMAIL',
            contactedBy: 'Fatima Al Hashmi',
            response: null,
            notes: 'Sent follow-up email requesting confirmation - no response'
          },
          {
            date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            method: 'PHONE',
            contactedBy: 'Ali Mohammed',
            response: 'NO_ANSWER',
            notes: 'Called partner - left voicemail'
          },
          {
            date: new Date().toISOString(),
            method: 'ESCALATION',
            contactedBy: 'Fatima Al Hashmi',
            response: null,
            notes: 'Escalated to finance manager for investigation'
          }
        ],
        notes: [
          {
            date: sevenDaysAgo.toISOString(),
            author: 'Ali Mohammed',
            note: 'Payment initiated - batch payment for 2 tickets'
          },
          {
            date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            author: 'Fatima Al Hashmi',
            note: 'Auto-confirm deadline reached - sending follow-up'
          },
          {
            date: new Date().toISOString(),
            author: 'Fatima Al Hashmi',
            note: 'Partner not responding - possible dispute or account issue'
          }
        ]
      },
      investigation: {
        required: true,
        reason: 'Payment overdue by 2 business days with no partner response',
        assignedTo: 'Khaled Rahman',
        assignedRole: 'Finance Manager',
        assignedDate: new Date().toISOString(),
        status: 'IN_PROGRESS',
        possibleReasons: [
          'Partner bank account issue',
          'Partner internal processing delay',
          'Dispute over payment amount',
          'Partner business closure/change'
        ]
      },
      actions: ['CONTACT_PARTNER', 'VIEW_BANK_STATEMENT', 'FORCE_CONFIRM', 'RAISE_DISPUTE', 'INVESTIGATE'],
      priority: 'URGENT'
    }
  ];
};

// 5. Channel Partner Commissions (Money OUT - Awaiting Approval)
export const getMockPendingCommissions = () => {
  const now = new Date();

  return [
    {
      id: 'commission-001',
      type: 'CHANNEL_COMMISSION_OUTGOING',
      channelPartnerId: 'channel-001',
      channelPartnerName: 'Al Fahim Business Services',
      amount: 38.50,
      currency: 'AED',
      status: 'PENDING_APPROVAL',
      moneyLocation: {
        inThaklisBank: true,
        inThaklisWallet: true,
        approvedForPayment: false,
        sentToPartner: false
      },
      commissionPeriod: {
        period: 'MONTHLY',
        month: now.toLocaleString('default', { month: 'long', year: 'numeric' }),
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
        daysInPeriod: new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate(),
        daysCompleted: now.getDate()
      },
      commissionBreakdown: {
        numberOfTickets: 7,
        totalTicketValue: 1540.00,
        commissionRate: 0.025,
        commissionAmount: 38.50,
        tickets: [
          { ticketId: 'TKT-2024-101', value: 200.00, commission: 5.00 },
          { ticketId: 'TKT-2024-102', value: 350.00, commission: 8.75 },
          { ticketId: 'TKT-2024-103', value: 180.00, commission: 4.50 },
          { ticketId: 'TKT-2024-104', value: 220.00, commission: 5.50 },
          { ticketId: 'TKT-2024-105', value: 240.00, commission: 6.00 },
          { ticketId: 'TKT-2024-106', value: 150.00, commission: 3.75 },
          { ticketId: 'TKT-2024-107', value: 200.00, commission: 5.00 }
        ]
      },
      requestDate: now.toISOString(),
      paymentDueDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
      daysUntilDue: new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate(),
      partnerBankDetails: {
        bankName: 'Emirates NBD',
        accountNumber: '****2345',
        accountName: 'Al Fahim Business Services LLC',
        iban: 'AE24****2345'
      },
      approval: {
        requestedBy: 'system',
        requiredApprovers: ['finance_manager'],
        approvalDeadline: new Date(now.getFullYear(), now.getMonth() + 1, -3).toISOString().split('T')[0]
      },
      priority: 'LOW',
      actions: ['APPROVE', 'REJECT', 'VIEW_DETAILS']
    },
    {
      id: 'commission-002',
      type: 'CHANNEL_COMMISSION_OUTGOING',
      channelPartnerId: 'channel-002',
      channelPartnerName: 'Emirates Business Hub',
      amount: 28.10,
      currency: 'AED',
      status: 'PENDING_APPROVAL',
      moneyLocation: {
        inThaklisBank: true,
        inThaklisWallet: true,
        approvedForPayment: false,
        sentToPartner: false
      },
      commissionPeriod: {
        period: 'MONTHLY',
        month: now.toLocaleString('default', { month: 'long', year: 'numeric' }),
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]
      },
      commissionBreakdown: {
        numberOfTickets: 5,
        totalTicketValue: 1124.00,
        commissionRate: 0.025,
        commissionAmount: 28.10,
        tickets: [
          { ticketId: 'TKT-2024-201', value: 280.00, commission: 7.00 },
          { ticketId: 'TKT-2024-202', value: 194.00, commission: 4.85 },
          { ticketId: 'TKT-2024-203', value: 250.00, commission: 6.25 },
          { ticketId: 'TKT-2024-204', value: 180.00, commission: 4.50 },
          { ticketId: 'TKT-2024-205', value: 220.00, commission: 5.50 }
        ]
      },
      requestDate: now.toISOString(),
      paymentDueDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0],
      daysUntilDue: new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate(),
      partnerBankDetails: {
        bankName: 'ADCB',
        accountNumber: '****8901',
        accountName: 'Emirates Business Hub',
        iban: 'AE27****8901'
      },
      approval: {
        requestedBy: 'system',
        requiredApprovers: ['finance_manager'],
        approvalDeadline: new Date(now.getFullYear(), now.getMonth() + 1, -3).toISOString().split('T')[0]
      },
      priority: 'LOW',
      actions: ['APPROVE', 'REJECT', 'VIEW_DETAILS']
    }
  ];
};

// Summary helper function - aggregates all pending approvals
export const getPendingApprovalsSummary = () => {
  const customerTopups = getMockPendingTopups();
  const partnerPayments = getMockPendingPartnerPayments();
  const approvedAwaitingTransfer = getMockApprovedAwaitingTransfer();
  const inTransit = getMockInTransitPayments();
  const commissions = getMockPendingCommissions();

  return {
    customerTopups: {
      total: customerTopups.reduce((sum, t) => sum + t.amount, 0),
      count: customerTopups.length,
      items: customerTopups,
      moneyStatus: {
        location: 'In Thaklis bank (unverified)',
        inBank: true,
        inWallet: false,
        verified: false
      },
      displayText: 'Money Status: ✓ In Thaklis bank, awaiting system recording',
      priority: 'HIGH',
      actionUrl: '/account/wallet?tab=1'
    },
    partnerPayments: {
      total: partnerPayments.reduce((sum, p) => sum + p.amount, 0),
      count: partnerPayments.length,
      items: partnerPayments,
      moneyStatus: {
        location: 'In Thaklis bank (reserved)',
        inBank: true,
        inWallet: true,
        approved: false,
        sent: false
      },
      displayText: 'Money Status: ✓ In Thaklis bank, reserved but not yet paid',
      priority: 'MEDIUM',
      actionUrl: '/account/wallet/partner-payments/pending'
    },
    approvedAwaitingTransfer: {
      total: approvedAwaitingTransfer.reduce((sum, p) => sum + p.amount, 0),
      count: approvedAwaitingTransfer.length,
      items: approvedAwaitingTransfer,
      moneyStatus: {
        location: 'In Thaklis bank (approved)',
        inBank: true,
        inWallet: true,
        approved: true,
        sent: false
      },
      displayText: 'Money Status: ✓ In Thaklis bank, approved - needs transfer',
      priority: 'HIGH',
      actionUrl: '/account/wallet/partner-payments/approved'
    },
    inTransit: {
      total: inTransit.reduce((sum, p) => sum + p.amount, 0),
      count: inTransit.length,
      items: inTransit,
      breakdown: {
        normal: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'NORMAL'),
        followUp: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'FOLLOW_UP_RECOMMENDED'),
        overdue: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'OVERDUE')
      },
      moneyStatus: {
        location: 'Left Thaklis bank (in transit)',
        inBank: false,
        inWallet: false,
        approved: true,
        sent: true,
        confirmed: false
      },
      displayText: 'Money Status: ✗ Left Thaklis bank, awaiting partner confirmation',
      alerts: {
        normal: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'NORMAL').length,
        followUp: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'FOLLOW_UP_RECOMMENDED').length,
        overdue: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'OVERDUE').length
      },
      priority: inTransit.some(p => p.reconciliation.reconciliationStatus === 'OVERDUE') ? 'URGENT' : 'MEDIUM',
      actionUrl: '/account/wallet/reconciliation'
    },
    channelCommissions: {
      total: commissions.reduce((sum, c) => sum + c.amount, 0),
      count: commissions.length,
      items: commissions,
      moneyStatus: {
        location: 'In Thaklis bank (Thaklis revenue)',
        inBank: true,
        inWallet: true,
        approved: false,
        sent: false
      },
      displayText: 'Money Status: ✓ In Thaklis bank, monthly payout pending',
      priority: 'LOW',
      actionUrl: '/account/wallet/commissions/pending'
    },
    totals: {
      totalAwaitingAction:
        customerTopups.length +
        partnerPayments.length +
        approvedAwaitingTransfer.length +
        inTransit.length +
        commissions.length,
      // Total = From Customers (IN) - To Service Partners (OUT) - To Channel Partners (OUT)
      totalAmount:
        customerTopups.reduce((sum, t) => sum + t.amount, 0) -
        inTransit.reduce((sum, p) => sum + p.amount, 0) -
        commissions.reduce((sum, c) => sum + c.amount, 0),
      urgentActions: inTransit.filter(p => p.reconciliation.reconciliationStatus === 'OVERDUE').length
    }
  };
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 2
  }).format(amount);
};

// Expose reset function globally for easy access in browser console
if (typeof window !== 'undefined') {
  window.resetMockWalletData = resetMockData;
  window.getPendingApprovalsSummary = getPendingApprovalsSummary;
  console.log('💡 Tip: Run window.resetMockWalletData() in console to reset mock data');
  console.log('💡 Tip: Run window.getPendingApprovalsSummary() to see pending approvals breakdown');
}
